import { useId, useState } from 'react';
import { IconChevronDown } from '@tabler/icons-react';

/**
 * Shared white card for tool controls and settings panels.
 *
 * `collapsible` turns the header into a disclosure button, for the long
 * panels — a settings card that is only opened once per sitting should not
 * push everything below it off the screen for the rest of the session.
 */
export default function ToolCard({
    title,
    headerAction,
    children,
    className = '',
    collapsible = false,
    defaultOpen = true,
}) {
    const [open, setOpen] = useState(defaultOpen);
    const bodyId = useId();
    const shown = !collapsible || open;

    return (
        <div
            className={`rounded-[20px] border border-[#ECE8E4] bg-white p-4 shadow-[0_1px_2px_rgba(28,24,21,0.04)] dark:border-zinc-700 dark:bg-zinc-900 ${className}`}
        >
            {title ? (
                <div className={`flex flex-none items-center justify-between gap-2 ${children && shown ? 'mb-2.5' : ''}`}>
                    {collapsible ? (
                        <button
                            type="button"
                            onClick={() => setOpen((v) => !v)}
                            aria-expanded={open}
                            aria-controls={bodyId}
                            className="-m-1 flex flex-1 items-center justify-between gap-2 rounded-lg p-1 text-left transition-colors hover:text-primary"
                        >
                            <span className="text-[13px] font-semibold text-[#3D3833] dark:text-zinc-200">{title}</span>
                            {/* Parked at the far end of the row, where a list of
                                stacked panels lines its arrows up in one column. */}
                            <IconChevronDown
                                size={16}
                                stroke={2.2}
                                className={`flex-none text-[#A8A099] transition-transform ${open ? '' : '-rotate-90'}`}
                            />
                        </button>
                    ) : (
                        <span className="text-[13px] font-semibold text-[#3D3833] dark:text-zinc-200">{title}</span>
                    )}
                    {headerAction}
                </div>
            ) : null}
            {/* Only the collapsible form wraps its body: a plain card's child is
                left untouched, since the cards that match the preview's height
                need their own flex child to reach this card's flex column.
                Closed panels stay mounted — they hold form state and file
                inputs that unmounting would throw away. */}
            {collapsible ? (
                <div id={bodyId} hidden={!shown}>
                    {children}
                </div>
            ) : children}
        </div>
    );
}
